import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useRouter } from 'next/router';

import { ArrowButton } from 'shared/components/Carousel';
import { SizeAndPrice } from 'productTemplate/components/buyBar/SwiperSlides/SizeAndPrice';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useSizeConversion } from 'productTemplate/hooks/useSizeConversion';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { setSize, setBigBuyBarView, addWindow } from 'store/slice/pdpStateSlice';
import getInitialSlideIndex from 'productTemplate/utils/buyBar/getInitialSlideIndex';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { fetcher } from 'shared/utils/swrDataFetcher';
import { requestService } from 'shared/utils/services/requestService';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import getConfig from 'next/config';
import { useLocation } from 'shared/hooks/useLocation';
import { IProduct } from 'shared/types/Product';

interface IBigBuyBarDesktopSwiperSectionProps {
  isBigBuyBarViewExpanded: boolean;
}

const getLongestPriceCount = (buyBarItems): number => {
  // Gets the price with most digits.
  // Helps to know if it pricing can fit in swiper
  const priceLengthArray = buyBarItems?.map((item) => {
    if (item?.lowestPriceCents?.amount === undefined) {
      return 0;
    }
    return String(item?.lowestPriceCents?.amount)?.length;
  });
  return Math.max(...priceLengthArray);
};

const getMaxSlidesPerViewNeeded = (buyBarItems) => {
  // Helps avoid long prices from overlapping.
  // If prices are too long, then reduce slides per view
  const priceDigitCountTolerance = 7; // digits
  const snugSlidesAmount = 7; // Slides count
  const spaciousSlidesAmount = 5; // Slides count

  if (buyBarItems?.length === 0) return snugSlidesAmount;

  const longestPriceCount = getLongestPriceCount(buyBarItems);
  if (longestPriceCount > priceDigitCountTolerance) return spaciousSlidesAmount;

  return snugSlidesAmount;
};

const fetchData = (url) => requestService({ url, method: 'GET' });

export const BigBuyBarDesktopSwiperSection = ({
  isBigBuyBarViewExpanded,
}: IBigBuyBarDesktopSwiperSectionProps) => {
  const storeDispatch = useStoreDispatch();
  const { query } = useRouter();
  const [swiper, setSwiper] = useState(null);
  const [showRightArrowForOneView, setShowRightArrowForOneView] = useState(false);
  const [showLeftArrowForOneView, setShowLeftArrowForOneView] = useState(false);
  const { convertedUserSize, isLoading } = useSizeConversion();
  const { productTemplate } = useStoreSelector((state) => state.pdp);
  const { allSizes, availableSizes, usedAvailableSizes } = useStoreSelector(
    (state) => state.pdp.buyBarVariants,
  );
  const isSpikeMitigateEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SPIKE_MITIGATION);
  const { publicRuntimeConfig } = getConfig();
  const { SPIKE_KEY } = publicRuntimeConfig;
  const { isOfferable } = useOffersContext();
  const { selectedCountryCode }: { selectedCountryCode: string } = useLocation();

  const isUnavailable = !isOfferable && availableSizes.length === 0;
  const buyBarItems = isUnavailable || isOfferable ? allSizes : availableSizes;
  const maxSlidesPerView = getMaxSlidesPerViewNeeded(buyBarItems);
  const hasPlentySlides = buyBarItems?.length > maxSlidesPerView;
  const initialSlideIndex = hasPlentySlides ? 0 : Math.floor(buyBarItems?.length / 2);
  const maxSlidesWithoutShowingArrow = maxSlidesPerView === 7 ? 4 : 3;

  const defaultUsedVariant = useRef<any>(null);

  const fetchUsedVariantAndOpenWindow = async (size) => {
    if (defaultUsedVariant.current) {
      return;
    }
    let res: any = [];
    const searchUrl = [
      '/web-api/v1/products/search',
      `?productTemplateId=${productTemplate?.id}&shoeCondition=${SHOE_CONDITIONS.USED}&size=${size}&sortBy=size&countryCode=${selectedCountryCode}pageLimit=1&page=1`,
    ].join('');

    try {
      if (isSpikeMitigateEnabled) {
        res = await fetcher({ url: searchUrl, key: SPIKE_KEY });
      } else {
        res = await fetchData(searchUrl);
      }
      if (res?.products?.length > 0) {
        defaultUsedVariant.current = res?.products?.[0];
        storeDispatch(addWindow(defaultUsedVariant.current as IProduct));
        storeDispatch(setBigBuyBarView('used'));
      }
    } catch {
      defaultUsedVariant.current = null;
    }
  };

  const handleTransitionEnd = () => {
    if (swiper?.enabled && hasPlentySlides) {
      // need to call internal swiper method here to update loop
      // this ensures that the slide animations to right/left are correct
      swiper?.loopFix();
    }
  };

  const showArrowsForOneView = () => {
    if (!hasPlentySlides && buyBarItems?.length > maxSlidesWithoutShowingArrow) {
      const showRightArrow = (swiper?.realIndex as number) < Math.floor(buyBarItems.length / 2);
      const showLeftArrow = (swiper?.realIndex as number) > buyBarItems.length / 2;

      setShowRightArrowForOneView(showRightArrow);
      setShowLeftArrowForOneView(showLeftArrow);
    }
  };

  const handleSlideChange = ({ realIndex }) => {
    const size = buyBarItems?.[realIndex]?.sizeOption?.value;
    if (size) storeDispatch(setSize(size));
    showArrowsForOneView();
  };

  useEffect(() => {
    if (swiper && swiper.enabled && buyBarItems?.length <= 0) {
      swiper.disable();
    }
  }, [swiper, buyBarItems?.length]);

  // Set the initial swiper value
  // if it's used then set to the first used size
  // If we have a user pref go to that, else go to the smallest available size, finally go to first or middle index
  useEffect(() => {
    const expandedSize = query?.expandedSize;
    const isUsed = query?.tab === 'used';

    const firstUsedSize = !isLoading && isUsed ? usedAvailableSizes?.[0]?.sizeOption?.value : null;

    const expandedSizeIndex =
      !isLoading && expandedSize
        ? buyBarItems.findIndex((size) => String(size?.sizeOption?.value) === String(expandedSize))
        : null;
    const convertedUserSizeIndex =
      convertedUserSize && !isLoading
        ? buyBarItems.findIndex(
            (size) => String(size?.sizeOption?.value) === String(convertedUserSize?.size),
          )
        : null;
    const canSlideToUserSize = convertedUserSizeIndex !== null && convertedUserSizeIndex !== -1;

    if (swiper && swiper.enabled) {
      if (expandedSizeIndex !== null && expandedSizeIndex !== -1) {
        storeDispatch(setSize(Number(expandedSize)));
        swiper?.slideToLoop(expandedSizeIndex, 700);
      } else if (firstUsedSize) {
        const firstUsedSizeIndex = buyBarItems.findIndex(
          (size) => size?.sizeOption?.value === firstUsedSize,
        );
        storeDispatch(setSize(firstUsedSize));
        storeDispatch(setBigBuyBarView('used'));

        // fetch used variant and open used window
        fetchUsedVariantAndOpenWindow(firstUsedSize);
        swiper?.slideToLoop(firstUsedSizeIndex, 700);
      } else if (canSlideToUserSize) {
        storeDispatch(setSize(Number(convertedUserSize?.size)));
        swiper?.slideToLoop(convertedUserSizeIndex, 700);
      } else {
        const index = getInitialSlideIndex(availableSizes, buyBarItems, hasPlentySlides);
        storeDispatch(setSize(buyBarItems?.[index]?.sizeOption?.value));
        swiper?.slideToLoop(index, 700);
      }

      showArrowsForOneView();
    }
  }, [swiper, convertedUserSize, isLoading, buyBarItems, usedAvailableSizes]);

  const prev = () => {
    swiper?.slideToLoop((swiper?.realIndex as number) - 4, 500);
  };

  const next = () => {
    swiper?.slideToLoop((swiper?.realIndex as number) + 4, 500);
  };

  return (
    <BigBuyBarDesktopSwiperSection.Wrapper $isBigBuyBarViewExpanded={isBigBuyBarViewExpanded}>
      {(hasPlentySlides || showLeftArrowForOneView) && (
        <BigBuyBarDesktopSwiperSection.ArrowButton
          onArrowClick={prev}
          direction="left"
          show
          $isBigBuyBarViewExpanded={isBigBuyBarViewExpanded}
          data-qa="swiper_left_arrow"
        />
      )}
      <BigBuyBarDesktopSwiperSection.Swiper
        centeredSlides
        freeMode
        freeModeSticky
        freeModeMomentumRatio={0.5}
        freeModeMomentumVelocityRatio={0.5}
        initialSlide={initialSlideIndex}
        loop={hasPlentySlides}
        loopAdditionalSlides={6}
        onSwiper={setSwiper}
        onTransitionEnd={handleTransitionEnd}
        onSlideChange={handleSlideChange}
        slideToClickedSlide
        slidesPerView={maxSlidesPerView}
        threshold={8}
      >
        {buyBarItems?.map(({ sizeOption, lowestPriceCents }, index) => {
          return (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={`${sizeOption?.presentation}-${index}-${lowestPriceCents?.amountUsdCents}`}
            >
              {({ isActive }) => (
                <SizeAndPrice
                  sizeOption={sizeOption}
                  lowestPriceCents={lowestPriceCents}
                  isActive={isActive}
                  isActiveAndExpanded={isActive && isBigBuyBarViewExpanded}
                  isCurrentlyUnavailable={isUnavailable}
                />
              )}
            </SwiperSlide>
          );
        })}
      </BigBuyBarDesktopSwiperSection.Swiper>
      {(hasPlentySlides || showRightArrowForOneView) && (
        <BigBuyBarDesktopSwiperSection.ArrowButton
          onArrowClick={next}
          direction="right"
          show
          $isBigBuyBarViewExpanded={isBigBuyBarViewExpanded}
          data-qa="swiper_right_arrow"
        />
      )}
    </BigBuyBarDesktopSwiperSection.Wrapper>
  );
};

BigBuyBarDesktopSwiperSection.Wrapper = styled.div<{ $isBigBuyBarViewExpanded }>`
  ${({ $isBigBuyBarViewExpanded }) =>
    $isBigBuyBarViewExpanded &&
    `
    border-bottom: 1px solid #000;
    `}
`;

BigBuyBarDesktopSwiperSection.Swiper = styled(Swiper)`
  width: 500px;
  max-width: 100%;
  max-height: 100vh;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

BigBuyBarDesktopSwiperSection.ArrowButton = styled(ArrowButton)<{ $isBigBuyBarViewExpanded }>`
  position: absolute;
  top: 30px;
  height: 45px;
  width: 0;
  opacity: 0;
  transition-property: width, opacity;
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
  ${({ $isBigBuyBarViewExpanded }) =>
    $isBigBuyBarViewExpanded &&
    `
    opacity: 1;
    width: 74px;
    `}
  opacity: ${({ $isBigBuyBarViewExpanded }) => $isBigBuyBarViewExpanded && '1'};
  z-index: ${({ theme }) => theme.zIndex.pdpFooter};
`;
